"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";

const BurnDistributionChart = ({ data }) => {
  const [distributionData, setDistributionData] = useState({
    ecosystem: 0,
    nodeRewards: 0,
    total: 0
  });

  useEffect(() => {
    if (data) {
      const ecosystem = parseFloat(data.total_burn_amt_ecosystem || 0);
      const nodeRewards = parseFloat(data.total_burn_amt_node_rewards || 0);

      setDistributionData({
        ecosystem: ecosystem,
        nodeRewards: nodeRewards,
        total: ecosystem + nodeRewards
      });
    }
  }, [data]);

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toLocaleString();
  };

  const formatPercent = (percent) => {
    // Show at least 4 decimal places if the number is very small
    if (percent < 0.01) return percent.toFixed(4) + '%';
    return percent.toFixed(2) + '%';
  };

  const getPercentage = (value, total) => {
    if (total === 0) return 0;
    return (value / total) * 100;
  };

  const ecosystemPercentage = getPercentage(distributionData.ecosystem, distributionData.total);
  const nodeRewardsPercentage = getPercentage(distributionData.nodeRewards, distributionData.total);

  // Create simple donut chart data
  const chartItems = [
    {
      label: "Ecosystem Burns",
      value: distributionData.ecosystem,
      percentage: ecosystemPercentage,
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-green-500/20",
      icon: "ph:tree-evergreen-bold"
    },
    {
      label: "Node Rewards Burns",
      value: distributionData.nodeRewards,
      percentage: nodeRewardsPercentage,
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-500/20",
      icon: "ph:database-bold"
    }
  ];

  if (!data) {
    return (
      <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 bg-[#23272e] rounded w-40"></div>
          </div>
          <div className="flex justify-center mb-6">
            <div className="w-32 h-32 bg-[#23272e] rounded-full"></div>
          </div>
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="h-4 bg-[#23272e] rounded w-24"></div>
                <div className="h-4 bg-[#23272e] rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">Burn Distribution</h3>
          <p className="text-sm text-gray-400">Distribution of token burns by category</p>
        </div>
        <Icon icon="ph:chart-pie-slice-bold" className="text-xl text-metaxseed-blue" />
      </div>

      {distributionData.total === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon icon="ph:chart-pie-slice-duotone" className="text-4xl text-gray-600 mb-4" />
          <p className="text-gray-400">No distribution data available</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Simple Donut Chart Visualization */}
          <div className="flex justify-center">
            <div className="relative w-32 h-32">
              {/* Background Circle */}
              <div className="absolute inset-0 rounded-full bg-[#0f1419]"></div>
              
              {/* Ecosystem Segment */}
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(from 0deg, #10b981 0%, #34d399 ${ecosystemPercentage}%, transparent ${ecosystemPercentage}%)`
                }}
              ></div>
              
              {/* Node Rewards Segment */}
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(from ${(ecosystemPercentage * 3.6)}deg, #3b82f6 0%, #06b6d4 ${nodeRewardsPercentage}%, transparent ${nodeRewardsPercentage}%)`
                }}
              ></div>
              
              {/* Center Circle */}
              <div className="absolute inset-4 rounded-full bg-[#161a23] flex items-center justify-center">
                <div className="text-center">
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-sm font-semibold text-white">
                    {formatNumber(distributionData.total)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Legend and Stats */}
          <div className="space-y-3">
            {chartItems.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-[#0f1419] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 ${item.bgColor} rounded-lg flex items-center justify-center`}>
                    <Icon icon={item.icon} className="text-sm text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{item.label}</p>
                    <p className="text-xs text-gray-400">{formatNumber(item.value)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent`}>
                    {formatPercent(item.percentage)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="pt-4 border-t border-[#2a2f38]">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-400">Dominant Category</p>
                <p className="text-sm font-semibold text-white">
                  {distributionData.ecosystem > distributionData.nodeRewards ? "Ecosystem" : "Node Rewards"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Distribution Balance</p>
                <p className="text-sm font-semibold text-green-400">
                  {Math.abs(ecosystemPercentage - nodeRewardsPercentage) <= 10 ? "Balanced" : "Skewed"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BurnDistributionChart; 